<template>
  <div class="chart-container">
    <line-chart
      v-if="loaded"
      :chartdata="chartData"
      :options="options"/>
  </div>
</template>

<script>

import log from 'util/log'
import { colorFromArray } from 'util/helpers'
import moment from 'moment'
import LineChart from './Chart.vue'
import tractor from './tractor.svg'

export default {
  name: 'ChartContainerComponent',
  components: { LineChart },
  props: {
    parcel: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    loaded: false,
    chartData: null
  }),
  computed: {
    options () {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          labels: {
            fontColor: 'white'
          }
        },
        tooltips: {
          mode: 'nearest',
          intersect: true,
          callbacks: {
            label: (tooltipItem, data) => {
              return tooltipItem.yLabel.toFixed(2)
            }
          }
        },
       hover: {
          mode: 'nearest',
          intersect: true
        },
        scales: {
          yAxes: [{
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$t('chart.y'),
              fontColor: 'white'
            },
            ticks: {
              min: 0,
              max: 1,
              stepSize: 0.1,
              fontColor: 'white'
            }
          }],
          xAxes: [{
            type: 'time',
            time: {
              tooltipFormat: 'DD.MM',
              unit: 'week',
              unitStepSize: 1,
              displayFormats: {
                'week': 'DD.MM'
              }
            },
            scaleLabel: {
              display: true,
              labelString: this.$t('chart.x'),
              fontColor: 'white'
            },
            ticks: {
              fontColor: 'white'
            }
          }]
        },
        onClick: (e, points) => {
          if (points.length && this.$conf.chart.lines[points[0]._datasetIndex].click) {
            this.clickHandle(this.chartData.datasets[points[0]._datasetIndex].data[points[0]._index].x)
          }
        }
      }
    },
    eventDates () {
      return this.parcel.events.map(evt => evt.date)
    }
  },
  mounted () {
    this.fillData()
  },
  methods: {
    fillData () {
      this.loaded = false
      const colorIndex = Object.keys(this.$conf.tilesets.colors)
      this.$store.dispatch('loadChartData', this.parcel[this.$conf.search.id])
        .then((result) => {
          if (result.length) {
            const start = moment(this.$conf.timebar.minDate)
            const end = moment(this.$conf.timebar.maxDate)
            const days = this.getRangeOfDates(start, end, 'day')
            this.chartData = {
              labels: [...days].map(d => d.format('YYYY-MM-DD')),
              lineAt: this.$conf.timebar.deadLines.map(d => {
                return {
                  value: d,
                  label: moment(d).format('DD.MM')
                }
              }),
              datasets: this.$conf.chart.lines.map(line => {
                const pointColors = []
                const borderColors = []
                const pointStyles = []
                const data = result[line.request].map(d => {
                  const m = moment(d[this.$conf.chart.request[line.request].dateColumn])
                  const pointDate = m.format('YYYY-MM-DD')
                  // cut marker for estimate probability line
                  if (line.id === 'estimate_probability') {
                    if (this.eventDates.indexOf(pointDate) > -1) {
                      const img = new Image()
                      img.src = tractor
                      pointStyles.push(img)
                    } else {
                      pointStyles.push('circle')
                    }
                  } else {
                    pointStyles.push('circle')
                  }
                  pointColors.push(line.point && line.point.color ? line.point.color : line.color)
                  borderColors.push(line.point && line.point.border ? line.point.border : line.color)
                  return {
                    x: pointDate,
                    y: d[line.id]
                  }
                })
                return {
                  backgroundColor: line.color,
                  borderColor: line.color,
                  borderWidth: 1,
                  pointRadius: line.point ? line.point.radius : 3,
                  pointBackgroundColor: pointColors,
                  pointBorderColor: borderColors,
                  pointStyle: pointStyles,
                  fill: false,
                  data: data,
                  lineTension: 0,
                  label: this.$t(`chart.legend.${line.id}`)
                }
              })
            }
            this.loaded = true
          }
        })
        .catch(err => {
          log('danger', 'Error: ' + err)
        })
    },
    getRangeOfDates (start, end, key, arr = [start.startOf(key)]) {
      if (start.isAfter(end)) {
        throw new Error('start must precede end')
      }
      const next = moment(start).add(1, key).startOf(key)
      if (next.isAfter(end, key)) {
        return arr
      }
      return this.getRangeOfDates(next, end, key, arr.concat(next))
    },
    clickHandle (e) {
      this.$store.dispatch('chartDate', e)
    }
  }
}
</script>

<style lang="stylus">
  .chart-container
    position: relative
    padding: 10px
    height: 100%
    > div
      position: relative
      height: 100%
</style>
